import React, { useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useProctorGroups } from '@indeed/frontend-proctor-utils';
import { Box, Heading, Skeleton } from '@indeed/ifl-components';
import { Alert9 } from '@indeed/indeed-design-system-illustrations-spot';
import { ApplicationPreview, SectionId } from '@indeed/jspl-job-applications';
import { useInitialState } from '../../../lib/context/InitialStateContext';
import GettextFactory from '../../../util/GettextFactory';
import { ApplicationTabContentFieldsFragment } from '@/pages/auto-apply/review/__generated__/ApplicationTabContentFragments.generated';
import { useApplicationPreview } from './useApplicationPreview';
import { useShowAiAutofillDisclaimer } from './useShowAiAutofillDisclaimer';
import { useDeleteAutoApplyApplicationDraft } from '../hooks/useDeleteAutoApplyApplicationDraft';
import { useEventLogger } from '../../../lib/eventLogger/EventLoggerProvider';
import { AvroOptional } from '@indeed/signals-schema/com/indeed/signals/schema/indeedapply/AutoApplyReviewButtonClickEvent';

/**
 * Valid editAction values accepted by Smart Apply webapp's directToEdit flow.
 * @see https://code.corp.indeed.com/indeedapply/smart-apply-webapp/-/blob/master/web/basecamp/static/scripts/smartapply/applyForm/util/directToEdit.ts
 */
type EditAction =
    | 'CONTACT_INFO'
    | 'RESUME'
    | 'RESUME_QUESTIONS'
    | 'QUESTIONS'
    | 'QUALIFICATION_QUESTIONS'
    | 'DEMOGRAPHIC_QUESTIONS'
    | 'INTERVIEW'
    | 'RELEVANT_EXPERIENCE'
    | 'DOCUMENTS'
    | 'CREDENTIAL_INFORMATION'
    | 'PROFILE_RESUME_REVIEW';

const SECTION_TO_EDIT_ACTION: Record<SectionId, EditAction> = {
    contactInfoSection: 'CONTACT_INFO',
    resumeSection: 'RESUME',
    screenerQuestionsSection: 'QUESTIONS',
    qualificationQuestionsSection: 'QUALIFICATION_QUESTIONS',
    demographicQuestionsSection: 'DEMOGRAPHIC_QUESTIONS',
    resumeQuestionsSection: 'RESUME_QUESTIONS',
    relevantExperienceSection: 'RELEVANT_EXPERIENCE',
    supportingDocumentsSection: 'DOCUMENTS',
    interviewSection: 'INTERVIEW',
    credentialInformationSection: 'CREDENTIAL_INFORMATION',
};

const SECTION_INTERNAL_ROUTES: Partial<Record<SectionId, (draftId: string) => string>> = {
    contactInfoSection: (id) => `/auto-apply/${id}/contact-info/edit`,
    resumeSection: (id) => `/auto-apply/${id}/resume/edit`,
    screenerQuestionsSection: (id) => `/auto-apply/${id}/questions/edit`,
};

export type ApplicationTabContentProps = {
    /** Fragment containing application data */
    fragment: ApplicationTabContentFieldsFragment;
    /** Callback to notify parent of preview state changes */
    onPreviewStateChange?: (
        isLoading: boolean,
        hasError: boolean,
        draftId: string | undefined,
    ) => void;
};

/**
 * Self-contained Application tab content.
 * Owns its own GraphQL operations, state, and edit handlers.
 */
const ApplicationTabContent: React.FC<ApplicationTabContentProps> = ({
    fragment,
    onPreviewStateChange,
}) => {
    const gt = GettextFactory();
    const navigate = useNavigate();
    const location = useLocation();
    const { draftId } = useParams<{ draftId: string }>();
    const [searchParams] = useSearchParams();
    const { origin, smartApplyBaseUrl } = useInitialState();
    const { getGroup } = useProctorGroups();
    const deleteAutoApplyApplicationDraft = useDeleteAutoApplyApplicationDraft();
    const eventLogger = useEventLogger();

    const editTogValue = getGroup('auto_apply_internal_edit_tog')?.value;
    const shouldRedirectToSA = editTogValue === -1 || editTogValue === 0;
    const showDemographicPreview = getGroup('ia_dq_questions_prefill_tst')?.value === 1;

    const jobId = fragment.applyableJob?.jobId;

    // Track if we've already initiated deletion for this draft to prevent duplicate deletes
    const deletedRef = useRef(false);

    // Check if this draft's preview already failed (from query params)
    // If so, skip the preview generation mutation
    const isPreviewFailed = useMemo(() => {
        const previewFailedDraftIdsParam = searchParams.get('previewFailedDraftIds');
        const previewFailedDraftIds = previewFailedDraftIdsParam
            ? previewFailedDraftIdsParam.split(',').filter(Boolean)
            : [];
        return draftId ? previewFailedDraftIds.includes(draftId) : false;
    }, [searchParams, draftId]);

    // `undefined` while resolving — useApplicationPreview defers the preview until it settles so the
    // mutation never runs with a stale value.
    const showAiAutofillDisclaimer = useShowAiAutofillDisclaimer(draftId, fragment.iaUid);

    const {
        html: decodedPreviewHtml,
        demographicQuestionsHtml,
        isLoading,
        error,
    } = useApplicationPreview({
        draftId,
        fragment,
        skipPreviewGeneration: isPreviewFailed,
        showAiAutofillDisclaimer,
    });

    const displayPreviewHtml = useMemo(() => {
        if (!decodedPreviewHtml || !showDemographicPreview || !demographicQuestionsHtml) {
            return decodedPreviewHtml;
        }

        const doc = new DOMParser().parseFromString(decodedPreviewHtml, 'text/html');
        const application = doc.querySelector('[data-testid="application"]');
        // Legacy DMS previews lack this container; match the preview module's fallback.
        if (!application) return decodedPreviewHtml;

        // Compose only for display. Cached metadata and employer-facing HTML remain separate.
        application.insertAdjacentHTML('beforeend', demographicQuestionsHtml);
        return doc.documentElement.outerHTML;
    }, [decodedPreviewHtml, demographicQuestionsHtml, showDemographicPreview]);

    // Notify parent of preview state changes
    useEffect(() => {
        onPreviewStateChange?.(isLoading, !!error, draftId);
    }, [isLoading, error, onPreviewStateChange, draftId]);

    useEffect(() => {
        if (error === 'permanent' && draftId && !deletedRef.current) {
            // Delete draft if failure is permanent (GraphQL error where retry won't help).
            // Track in ref to prevent duplicate deletes if dependencies change.
            // Auto-delete (system cleanup), not the jobseeker rejecting the job — pass null so the
            // draft is removed WITHOUT disliking the job; only user-clicked deletes dislike.
            deletedRef.current = true;
            // Best-effort cleanup — swallow a failed delete (the hook now rejects on failure).
            void deleteAutoApplyApplicationDraft(draftId, null).catch(() => {});
        }
    }, [error, draftId, deleteAutoApplyApplicationDraft]);

    const buildSAEditUrl = (editAction: EditAction, indeedApplyableJobId: string): string => {
        const returnOnEditUrl = new URL(`${location.pathname}${location.search}`, origin).href;
        const params = new URLSearchParams({
            indeedApplyableJobId,
            editAction,
            returnOnEditUrl,
        });
        return `${smartApplyBaseUrl}/beta/indeedapply/applybyapplyablejobid?${params.toString()}`;
    };

    const handleEditSection = (sectionId: SectionId) => {
        const editAction = SECTION_TO_EDIT_ACTION[sectionId];

        eventLogger.logReviewButtonClick?.({
            currentPage: location.pathname,
            button: 'app-tab-edit-section',
            draftId: AvroOptional.string(draftId),
            metadata: { string: editAction },
        });

        const internalRoute = draftId && SECTION_INTERNAL_ROUTES[sectionId]?.(draftId);
        // Screener questions always open the internal /questions/edit page — it is their purpose-built
        // destination, so it bypasses the auto_apply_internal_edit_tog gate (contact-info and resume
        // stay gated: they fall back to Smart Apply when the toggle is off).
        //
        // INTENTIONAL bypass (not an oversight): `auto_apply_internal_edit_tog` gates the internal
        // edit experience for contact-info/resume, but the screener questions have no Smart Apply
        // fallback worth routing to — sending them to SA was the bug this fixes — so they are always
        // internal regardless of the toggle's value (including inactive/control). Reviewers' proctor
        // checks flag this; it is expected until the toggle is retired for this section.
        const forceInternal = sectionId === 'screenerQuestionsSection';
        if (internalRoute && (forceInternal || !shouldRedirectToSA)) {
            void navigate(`${internalRoute}${location.search}`);
            return;
        }

        if (!jobId) return;

        window.location.href = buildSAEditUrl(editAction, jobId);
    };

    // Show error UI if there's a new error OR if this draft previously failed
    if (error || isPreviewFailed) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    paddingBlock: 8,
                    paddingInline: 4,
                    blockSize: '100%',
                }}
            >
                <Alert9 />
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 4,
                    }}
                >
                    <Heading level={3}>
                        {gt.gettext("We can't load a preview of your application")}
                    </Heading>
                </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box
                role="status"
                aria-label={gt.gettext('Loading application preview')}
                sx={{ padding: 4 }}
            >
                <Skeleton variant="text" sx={{ marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '70%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '85%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '50%', marginBlockEnd: 6 }} />
                <Skeleton variant="text" sx={{ inlineSize: '85%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '70%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '85%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '40%', marginBlockEnd: 6 }} />
                <Skeleton variant="text" sx={{ inlineSize: '85%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '70%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '75%', marginBlockEnd: 2 }} />
                <Skeleton variant="text" sx={{ inlineSize: '40%' }} />
            </Box>
        );
    }

    return displayPreviewHtml ? (
        <Box sx={{ paddingInlineStart: 2, paddingBlockStart: 2, paddingInlineEnd: 4 }}>
            <ApplicationPreview html={displayPreviewHtml} onEditSection={handleEditSection} />
        </Box>
    ) : null;
};

export default ApplicationTabContent;
